import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from '@mui/material';
import { useQuery } from 'react-query';
import { analyticsAPI } from '../../services/api';
import LoadingSpinner from '../../components/Common/LoadingSpinner';

const SubTabPanel = ({ children, value, index, ...other }) => (
  <div role="tabpanel" hidden={value !== index} {...other}>
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

const AnalyticsContent = () => {
  const [subTabValue, setSubTabValue] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [filters, setFilters] = useState({ country: '', businessType: '' });

  const { data: overviewData, isLoading: overviewLoading } = useQuery(
    ['analytics-overview', dateRange, filters],
    () => analyticsAPI.getOverview({
      startDate: dateRange.startDate?.toISOString(),
      endDate: dateRange.endDate?.toISOString(),
      ...filters
    }),
    { refetchInterval: 5 * 60 * 1000 }
  );

  const { data: geoData, isLoading: geoLoading } = useQuery('analytics-geographic', () => analyticsAPI.getGeographic());
  const { data: businessData, isLoading: businessLoading } = useQuery('analytics-business-types', () => analyticsAPI.getBusinessTypes());

  if (overviewLoading || geoLoading || businessLoading) {
    return <LoadingSpinner message="Loading analytics..." />;
  }

  const overview = overviewData?.data?.overview || {};
  const geographic = geoData?.data?.distribution || [];
  const businessTypes = businessData?.data?.distribution || [];

  return (
    <Box>
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Country</InputLabel>
                <Select
                  value={filters.country}
                  label="Country"
                  onChange={(e) => setFilters(prev => ({ ...prev, country: e.target.value }))}
                >
                  <MenuItem value="">All Countries</MenuItem>
                  {geographic.slice(0, 10).map((country) => (
                    <MenuItem key={country._id} value={country._id}>
                      {country._id} ({country.count})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Business Type</InputLabel>
                <Select
                  value={filters.businessType}
                  label="Business Type"
                  onChange={(e) => setFilters(prev => ({ ...prev, businessType: e.target.value }))}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {businessTypes.slice(0, 10).map((type) => (
                    <MenuItem key={type._id} value={type._id}>
                      {type._id} ({type.count})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Sub-tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={subTabValue} onChange={(e, v) => setSubTabValue(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Overview" />
          <Tab label="Geographic" />
          <Tab label="Business Types" />
          <Tab label="Usage Patterns" />
        </Tabs>
      </Box>

      {/* Overview Sub-Tab */}
      <SubTabPanel value={subTabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Installation Summary</Typography>
                <Typography variant="h4" color="primary">{overview.totalInstallations?.toLocaleString() || 0}</Typography>
                <Typography variant="body2" color="text.secondary">Total Installations</Typography>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h6">{overview.freeUsers?.toLocaleString() || 0}</Typography>
                    <Typography variant="body2" color="text.secondary">Free Users</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h6">{overview.paidUsers?.toLocaleString() || 0}</Typography>
                    <Typography variant="body2" color="text.secondary">Paid Users</Typography>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Conversion Rate: {overview.conversionRate || 0}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>User Engagement</Typography>
                <Typography variant="h4" color="secondary">{Math.round(overview.averageDailyActiveUsers || 0)}</Typography>
                <Typography variant="body2" color="text.secondary">Average Daily Active Users</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </SubTabPanel>

      {/* Geographic Sub-Tab */}
      <SubTabPanel value={subTabValue} index={1}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Geographic Distribution</Typography>
            {geographic.slice(0, 10).map((country, index) => (
              <Box key={country._id} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: index < 9 ? 1 : 0, borderColor: 'divider' }}>
                <Typography variant="body1">{index + 1}. {country._id}</Typography>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body1" fontWeight="bold">{country.count.toLocaleString()}</Typography>
                  <Typography variant="body2" color="text.secondary">Free: {country.freeUsers} | Paid: {country.paidUsers}</Typography>
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>
      </SubTabPanel>

      {/* Business Types Sub-Tab */}
      <SubTabPanel value={subTabValue} index={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Business Type Distribution</Typography>
            {businessTypes.slice(0, 10).map((type, index) => (
              <Box key={type._id} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: index < 9 ? 1 : 0, borderColor: 'divider' }}>
                <Typography variant="body1">{index + 1}. {type._id?.replace('_', ' ').toUpperCase()}</Typography>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body1" fontWeight="bold">{type.count.toLocaleString()}</Typography>
                  <Typography variant="body2" color="text.secondary">Avg Usage: {Math.round(type.avgUsage || 0)} min</Typography>
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>
      </SubTabPanel>

      {/* Usage Patterns Sub-Tab */}
      <SubTabPanel value={subTabValue} index={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Usage Patterns</Typography>
            <Typography variant="body2" color="text.secondary">
              Detailed usage pattern analytics including feature adoption rates, session duration patterns, user retention metrics, peak usage times, and screen navigation patterns.
            </Typography>
          </CardContent>
        </Card>
      </SubTabPanel>
    </Box>
  );
};

export default AnalyticsContent;

